import { createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { default as jwtDecode } from 'jwt-decode';
import cookies from '../utils/cookies';
import * as Sentry from '@sentry/browser';

const vendor = window.localStorage.getItem('pass_vendor') || null;

const token = cookies.get('auth_token');
const isAuth = !!token;
export const initialState = {
    isLoading: true,
    loading: true, // legacy, replace with isLoading
    enableEditing: true,
    selectedVendor: vendor,
    selectedVendorName: null,
    isAuth,
    session: isAuth
        ? jwtDecode(token)
        : {
              User: {}
          }
};

export const getAccount = (username) => {
    return axios
        .request({
            url: `${process.env.REACT_APP_API_URL}/authorized/users/${username}`,
            method: 'GET'
        })
        .then((resp) => resp.data);
};

export const getPublishers = (pubs = null) => {
    return axios
        .request({
            url: `${process.env.REACT_APP_API_URL}/authorized/mb_publishers`,
            method: 'get',
            params: {
                pubs
            }
        })
        .then((resp) => resp.data);
};

export const getVendor = (vendor) => {
    return axios
        .request({
            url: `${process.env.REACT_APP_API_URL}/authorized/vendors/${vendor.id}`,
            method: 'GET'
        })
        .then((resp) => ({ ...resp.data, ...vendor }));
};

export const getVendors = (ids) => {
    return Promise.all(ids.map(getVendor));
};

export const loadAccount = async (tok = null) => {
    let token = tok || cookies.get('auth_token');
    let isAuth = !!token;
    let session = isAuth ? jwtDecode(token) : null;
    let activeVendor;
    let vendors = [];
    let publishers = [];

    if (session) {
        let userAccount = null;
        try {
            userAccount = await getAccount(session['cognito:username']);
        } catch (err) {
            userAccount = {};
            console.error(err);
        }
        if (!userAccount.address) {
            userAccount.address = {
                street: [],
                city: '',
                state: '',
                postal_code: ''
            };
        }
        Sentry.setUser({
            email: userAccount.email,
            id: userAccount.id,
            username: userAccount.username
        });
        try {
            vendors = await getVendors(userAccount.provider.vendors);
            userAccount.vendors = vendors;
            activeVendor = vendors.find((v) => v.id === Number(vendor));

            if (vendors.length === 1) {
                activeVendor = vendors[0];
                localStorage.setItem('pass_vendor', activeVendor.id);
            }
        } catch (err) {
            userAccount.vendors = [];
            console.error(err);
        }
        try {
            publishers = await getPublishers();
            userAccount.publishers = publishers.results;
        } catch (err) {
            userAccount.publishers = [];
            console.error(err);
        }
        return {
            isAuthenticated: isAuth,
            session,
            loading: false,
            user: { ...userAccount },
            selectedVendor: activeVendor ? activeVendor.id : null,
            selectedVendorName: activeVendor ? activeVendor.vendorname : null,
            isAdmin: userAccount.passportadmin,
            isReadOnly: userAccount.readonly ? true : !userAccount.provider?.singletitleupload
        };
    }
};

export const sessionSlice = createSlice({
    name: 'session',
    initialState,
    reducers: {
        //PERSIST_LOGIN_START
        sessionLoginStart: (state, action) => {
            state.isLoading = true;
        },
        //FIRST_LOGIN_SUCCESS
        //PERSIST_LOGIN_SUCCESS
        //LOGIN_SUCCESS
        sessionLoginSuccess: (state, action) => {
            return {
                ...state,
                isLoading: false,
                loading: false,
                ...action.payload
            };
        },
        //PERSIST_LOGIN_FAIL
        sessionLoginFailure: (state) => {
            state.isLoading = false;
            state.loading = false;
            state.user = {};
            state.isAdmin = false;
            state.isReadOnly = true;
        },
        //COMPLETE_NEW_PASSWORD_SUCCESS
        sessionCompleteNewPasswordSuccess: (state) => {
            state.user = null;
            state.isLoading = false;
        },
        //COMPLETE_NEW_PASSWORD_TIMEOUT
        sessionCompleteNewPasswordTimeout: (state) => {
            state.user = null;
        },
        //LOGOUT
        sessionLogout: () => {
            return { ...initialState, isLoading: false, loading: false, session: {} };
        },
        //REFRESH_USER
        sessionUpdateUser: (state, action) => {
            state.user = {
                ...state.user,
                ...action.payload
            };
        },
        sessionUpdate: (state, action) => {
            return { ...state, ...action.payload };
        }
    }
});

export const {
    sessionLoginStart,
    sessionLoginSuccess,
    sessionLoginFailure,
    sessionCompleteNewPasswordSuccess,
    sessionCompleteNewPasswordTimeout,
    sessionLogout,
    sessionUpdate,
    sessionUpdateUser
} = sessionSlice.actions;

export default sessionSlice.reducer;
